import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Users as UsersIcon, Shield, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { authFetch, API } from '../utils/api';

function TeamManagement({ user }) {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await authFetch(`${API}/team/employees`);
      if (response.ok) {
        const data = await response.json();
        setEmployees(data);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      const response = await authFetch(`${API}/team/employees/${userId}/role`, {
        method: 'POST',
        body: JSON.stringify({ role: newRole })
      });

      if (response.ok) {
        toast.success('Role updated successfully');
        fetchEmployees();
      } else {
        toast.error('Failed to update role');
      }
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  if (user.role !== 'admin') {
    return (
      <Layout user={user}>
        <div className="text-center py-12">
          <p className="text-foreground-muted">You don't have permission to access this page</p>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <p className="text-foreground-muted">Loading...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div data-testid="team-page">
        <h1 className="text-3xl font-heading font-bold text-foreground-DEFAULT mb-6">Team Management</h1>

        <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6">
          {employees.length === 0 ? (
            <div className="text-center py-12">
              <UsersIcon className="w-12 h-12 text-foreground-muted mx-auto mb-4" />
              <p className="text-foreground-muted">No employees yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {employees.map((employee) => (
                <div
                  key={employee.user_id}
                  className="bg-slate-50 rounded-xl p-6"
                  data-testid={`employee-${employee.user_id}`}
                >
                  <div className="flex items-start gap-3 mb-4">
                    <div className="relative flex-shrink-0">
                      {employee.picture ? (
                        <>
                          <img
                            src={employee.picture}
                            alt={employee.name}
                            className="w-12 h-12 rounded-full object-cover border border-slate-200"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextElementSibling.style.display = 'flex';
                            }}
                          />
                          <div className="w-12 h-12 rounded-full bg-primary/10 items-center justify-center hidden">
                            <User className="w-6 h-6 text-primary" />
                          </div>
                        </>
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="w-6 h-6 text-primary" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-foreground-DEFAULT truncate">{employee.name}</h3>
                      <p className="text-sm text-foreground-muted truncate">{employee.email}</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {employee.role === 'admin' && <Shield className="w-4 h-4 text-primary" />}
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          employee.role === 'admin'
                            ? 'bg-primary/10 text-primary'
                            : 'bg-slate-200 text-slate-700'
                        }`}
                      >
                        {employee.role}
                      </span>
                    </div>

                    {employee.user_id !== user.user_id && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          handleRoleChange(employee.user_id, employee.role === 'admin' ? 'employee' : 'admin')
                        }
                        className="rounded-full text-xs"
                        data-testid={`toggle-role-${employee.user_id}`}
                      >
                        {employee.role === 'admin' ? 'Remove Admin' : 'Make Admin'}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default TeamManagement;